import React, {
  useState, useEffect, forwardRef, useImperativeHandle,
} from 'react';
import { get, set } from '../../../utility/utility';
import CoreFormDualAction from '../../core/form/CoreFormDualAction';
import { INSPECTION_CREATE_FORM_MODES } from '../../../utility/InspectionsUtils';
import {
  renderInputField, renderInputFieldWithSuggestions, validateForm,
} from '../../core/input/CoreInputUtils';

const fieldsToValidate = ['commodity', 'applicantName'];
const InspectionFormCreate = forwardRef(({
  organization,
  inspectionsPostIsLoading,
  dispatchInspectionsPost,
  dispatchInspectionsPut,
  inspectionsPutIsLoading,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentInspection, setCurrentInspection] = useState(null);
  const [inputState, setInputState] = useState({});
  const [modeState, setModeState] = useState(null);
  const [error, setError] = useState({});
  const [commodityOptionsState, setCommodityOptionsState] = useState([]);
  const [contactOptionsState, setContactOptionsState] = useState([]);

  useImperativeHandle(ref, () => ({
    open: (inspection, mode) => {
      setCurrentInspection(inspection);
      const { applicantName, commodity, purchaseOrder } = get(inspection, 'summary', {});
      setInputState({
        applicantName,
        commodity,
        purchaseOrder,
      });
      setIsOpen(true);
      setModeState(mode);
      setError({});

      // Extract the commodity options
      const rawCommodities = get(organization, 'inspectionSettings.commodities', []);
      const commodityOptions = rawCommodities.map((c) => ({
        label: get(c, 'commodity').trim(),
        value: get(c, '_id'),
      }));
      setCommodityOptionsState(commodityOptions);

      // Extract the applicant contact options
      const rawContacts = get(organization, 'contacts', []);
      const contactOptions = rawContacts.map((c) => ({
        label: get(c, 'company.name').trim(),
        value: get(c, '_id'),
      }));
      setContactOptionsState(contactOptions);
    },
    close: () => setIsOpen(false),
  }));

  const closeModal = () => {
    setIsOpen(false);
  };
  useEffect(() => {
    if (!inspectionsPostIsLoading || !inspectionsPutIsLoading) {
      closeModal();
    }
  }, [inspectionsPostIsLoading, inspectionsPutIsLoading]);

  if (!isOpen) return null;

  const onInputChange = (event, path) => {
    const newState = { ...inputState };
    newState[path] = event.target.value;
    setInputState(newState);
    validateForm({
      state: newState,
      fieldsToValidate,
      setError,
    });
  };
  const handleSave = () => {
    const validation = validateForm({
      state: inputState,
      fieldsToValidate,
      setError,
    });
    if (validation) {
      const serviceId = get(organization, 'inspectionSettings.services.0._id', null);
      const request = {
        inspectorOrganizationId: organization._id,
        serviceId,
      };

      if (modeState === INSPECTION_CREATE_FORM_MODES.new) {
        dispatchInspectionsPost({
          request,
          commodity: inputState.commodity,
          applicantName: inputState.applicantName,
          purchaseOrder: inputState.purchaseOrder,
        });
      } else if (modeState === INSPECTION_CREATE_FORM_MODES.edit) {
        const body = {};

        set(body, 'participants.applicant.name', get(inputState, 'applicantName'));
        set(body, 'shipment.logistics.commodity', get(inputState, 'commodity'));
        set(body, 'shipment.logistics.purchaseOrder', get(inputState, 'purchaseOrder'));

        dispatchInspectionsPut(
          currentInspection,
          body,
        );
      }
    }
  };

  const renderTitle = (mode) => {
    switch (mode) {
      case INSPECTION_CREATE_FORM_MODES.new:
        return 'New Inspection';
      case INSPECTION_CREATE_FORM_MODES.edit:
        return 'Edit Inspection';
      case INSPECTION_CREATE_FORM_MODES.duplicate:
        return 'Duplicate Inspection';
      default:
        return 'Inspection';
    }
  };

  return (
    <CoreFormDualAction
      title={renderTitle(modeState)}
      primaryButtonCaption="Save"
      primaryButtonCallback={() => handleSave(currentInspection, inputState)}
      primaryButtonCallbackStatus={inspectionsPostIsLoading || inspectionsPutIsLoading}
      primaryButtonDisabled={Object.keys(error).length > 0 || inspectionsPostIsLoading || inspectionsPutIsLoading}
      secondaryButtonCaption="Cancel"
      secondaryButtonCallback={closeModal}
    >
      {renderInputFieldWithSuggestions({
        id: 'commodity', label: 'Commodity Description', placeholder: 'Commodity', options: commodityOptionsState, inputState, onInputChange, error,
      })}
      {renderInputFieldWithSuggestions({
        id: 'applicantName', label: 'Applicant Name', placeholder: 'Applicant', options: contactOptionsState, inputState, onInputChange, error,
      })}
      {renderInputField({
        id: 'purchaseOrder', inputState, onInputChange, error,
      })}
    </CoreFormDualAction>
  );
});

export default InspectionFormCreate;
